/**
 * 代码编辑器：CodeMirror 6 的薄包装（旧 components/common/CodeMirrorEditor）。
 * 受控组件；按实际主题在亮色默认主题和 one-dark 之间切换。
 */
import { useEffect, useRef } from 'react'
import { basicSetup } from 'codemirror'
import { Compartment, EditorState } from '@codemirror/state'
import { EditorView, placeholder as placeholderExtension } from '@codemirror/view'
import { javascript } from '@codemirror/lang-javascript'
import { oneDark } from '@codemirror/theme-one-dark'
import { useAppliedTheme, type AppliedTheme } from '@/stores/theme'
import styles from './CodeEditor.module.css'

interface CodeEditorProps {
  value: string
  onChange?: (value: string) => void
  // json / javascript 用 JavaScript 语法高亮（与旧版一致），其余为纯文本
  language?: 'json' | 'javascript' | 'text'
  height?: number
  placeholder?: string
  readOnly?: boolean
}

function themeExtension(appliedTheme: AppliedTheme) {
  return appliedTheme === 'dark' ? oneDark : []
}

export function CodeEditor({ value, onChange, language = 'json', height = 200, placeholder, readOnly = false }: Readonly<CodeEditorProps>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const themeCompartment = useRef(new Compartment())
  const onChangeRef = useRef(onChange)
  const appliedTheme = useAppliedTheme()

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // 挂载时创建编辑器，卸载时销毁；value 与主题变化在下面的 effect 里同步
  useEffect(() => {
    const view = new EditorView({
      parent: containerRef.current!,
      state: EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          EditorView.lineWrapping,
          language === 'text' ? [] : javascript(),
          placeholder ? placeholderExtension(placeholder) : [],
          EditorState.readOnly.of(readOnly),
          EditorView.editable.of(!readOnly),
          themeCompartment.current.of(themeExtension(appliedTheme)),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) onChangeRef.current?.(update.state.doc.toString())
          })
        ]
      })
    })
    viewRef.current = view
    return () => {
      view.destroy()
      viewRef.current = null
    }
    // 编辑器只创建一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 外部 value 变化时替换内容（用户输入引起的变化两边已一致，不重复派发）
  useEffect(() => {
    const view = viewRef.current
    if (view && view.state.doc.toString() !== value) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } })
    }
  }, [value])

  useEffect(() => {
    viewRef.current?.dispatch({ effects: themeCompartment.current.reconfigure(themeExtension(appliedTheme)) })
  }, [appliedTheme])

  return <div ref={containerRef} className={styles.editor} style={{ height }} data-theme={appliedTheme} />
}
